import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { adminApi } from "../adminApi";
import { currentPhase } from "../../lib/harvestDates";

const TABS = [
  { to: "/admin/harvest", label: "Timeline", end: true, testid: "harvest-tab-timeline" },
  { to: "/admin/harvest/cadences", label: "Cadences", testid: "harvest-tab-cadences" },
  { to: "/admin/harvest/accounts", label: "Accounts", testid: "harvest-tab-accounts" },
  { to: "/admin/harvest/fulfillment", label: "Fulfillment", testid: "harvest-tab-fulfillment" },
  { to: "/admin/harvest/approvals", label: "Approvals", testid: "harvest-tab-approvals" },
  { to: "/admin/harvest/kpis", label: "KPIs", testid: "harvest-tab-kpis" },
];

/** Shared chrome for every One Harvest Season screen: title, phase strip and sub-nav. */
export const HarvestShell = ({ settings, pending, children }) => {
  const location = useLocation();
  const phase = currentPhase(settings?.phases || []);

  useEffect(() => {
    document.title = "One Harvest Season — Growth Desk";
  }, [location.pathname]);

  return (
    <div className="space-y-7" data-testid="harvest-page">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
          Season 2026 · Nov 2 – Jan 29
        </p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-serif text-3xl font-light tracking-tight text-cream sm:text-4xl">
            One Harvest Season
          </h1>
          <div className="flex flex-wrap items-center gap-3" data-testid="harvest-phase-strip">
            <span className="rounded-full border border-sage/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-sage">
              {phase.state === "upcoming" ? `Next: ${phase.name}` : phase.name}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/45">
              {phase.state === "upcoming"
                ? `${phase.daysLeft} days to start`
                : `${phase.daysLeft} days left`}
            </span>
            {pending > 0 ? (
              <span
                data-testid="harvest-pending-badge"
                className="rounded-full bg-gold/20 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold"
              >
                {pending} awaiting approval
              </span>
            ) : null}
          </div>
        </div>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cream/50">
          Apollo and Thanks.io run the season. This desk mirrors them: nothing here sends email or
          mail, approvals are recorded for your own records, and every gift is capped per recipient.
        </p>
      </header>

      <nav className="flex flex-wrap gap-1 border-b border-cream/10 pb-3">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            data-testid={tab.testid}
            className={({ isActive }) =>
              `rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                isActive ? "bg-cream/10 text-cream" : "text-cream/45 hover:bg-cream/5 hover:text-cream"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {children}
    </div>
  );
};

/** Loads settings + the pending-approval count once per screen. */
export const useHarvestHeader = () => {
  const [settings, setSettings] = useState(null);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    adminApi.get("/crm/harvest/settings").then(({ data }) => setSettings(data)).catch(() => {});
    adminApi.get("/crm/harvest/approvals")
      .then(({ data }) => setPending(data.pending || 0))
      .catch(() => {});
  }, []);

  return { settings, pending };
};
